import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from './Layout';
import {
  BarChart3, TrendingUp, Users, Activity, Download, Globe, Monitor, Smartphone,
  Tablet, Tv, Wifi, Cpu, HardDrive, Clock, FileText, MapPin, RefreshCw,
  AlertCircle, ChevronDown
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area
} from 'recharts';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { toast } from 'react-toastify';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const TABS = [
  { id: 'overview',   label: 'Resumen',      icon: BarChart3 },
  { id: 'realtime',   label: 'Tiempo Real',  icon: Activity },
  { id: 'audience',   label: 'Audiencia',    icon: Users },
  { id: 'traffic',    label: 'Tráfico',      icon: TrendingUp },
  { id: 'devices',    label: 'Dispositivos', icon: Monitor },
  { id: 'geo',        label: 'Geografía',    icon: Globe },
  { id: 'export',     label: 'Exportar',     icon: Download },
];

const TOOLTIP_STYLE = {
  contentStyle: {
    background: 'rgba(17,24,39,0.97)',
    border: '1px solid rgba(129,140,248,0.3)',
    borderRadius: '12px',
    backdropFilter: 'blur(12px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
  },
  labelStyle: { color: '#fff', fontWeight: 'bold' }
};

const NO_DATA_PLACEHOLDER = (msg = 'Sin datos para este período') => (
  <div className="flex flex-col items-center justify-center py-16 text-gray-500">
    <AlertCircle size={40} className="mb-3 opacity-40" />
    <p className="text-sm">{msg}</p>
    <p className="text-xs mt-1 opacity-60">Los datos aparecerán cuando haya visitas desde el player embebido</p>
  </div>
);

// ── Detect device type ───────────────────────────────────────────────────────
function detectDevice() {
  const ua = navigator.userAgent.toLowerCase();
  if (/smart-tv|smarttv|googletv|appletv|hbbtv|roku|firetv/.test(ua)) return 'smarttv';
  if (/ipad|tablet|(android(?!.*mobile))/.test(ua)) return 'tablet';
  if (/mobile|android|iphone|ipod|blackberry|windows phone/.test(ua)) return 'mobile';
  return 'desktop';
}

function Analytics({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // Data states
  const [stats, setStats]               = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [contentDist, setContentDist]   = useState([]);
  const [trends, setTrends]             = useState({ channels: 0, viewers: 0 });
  const [realTime, setRealTime]         = useState(null);
  const [retention, setRetention]       = useState({ data: [], hasData: false });
  const [trafficPeaks, setTrafficPeaks] = useState({ data: [], hasData: false });
  const [devices, setDevices]           = useState({ data: [], hasData: false });
  const [geo, setGeo]                   = useState({ data: [], hasData: false });
  const [avgWatch, setAvgWatch]         = useState({ data: [], hasData: false });
  const [mapTooltip, setMapTooltip]     = useState(null);

  const rtTimerRef = useRef(null);

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  // ── Load all period-dependent data ────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const p = `?period=${period}`;
      const [sumRes, actRes, distRes, trRes, retRes, tpRes, devRes, geoRes, awRes] =
        await Promise.all([
          axios.get(`/api/analytics/summary${p}`, authHeader()),
          axios.get(`/api/analytics/chart/activity${p}`, authHeader()),
          axios.get('/api/analytics/chart/content-distribution', authHeader()),
          axios.get('/api/analytics/trends', authHeader()),
          axios.get(`/api/analytics/retention${p}`, authHeader()),
          axios.get(`/api/analytics/traffic-peaks${p}`, authHeader()),
          axios.get(`/api/analytics/devices${p}`, authHeader()),
          axios.get(`/api/analytics/geo${p}`, authHeader()),
          axios.get(`/api/analytics/avg-watch-time${p}`, authHeader()),
        ]);

      setStats(sumRes.data);
      setActivityData(actRes.data.data || []);
      setContentDist(distRes.data.data || []);
      setTrends(trRes.data);
      setRetention(retRes.data);
      setTrafficPeaks(tpRes.data);
      setDevices(devRes.data);
      setGeo(geoRes.data);
      setAvgWatch(awRes.data);
    } catch (e) {
      console.error('Error cargando analíticas:', e);
      toast.error('Error al cargar analíticas');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Real-time polling every 5s ────────────────────────────────────────────
  const loadRealTime = useCallback(async () => {
    try {
      const res = await axios.get('/api/analytics/real-time', authHeader());
      setRealTime(res.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadRealTime();
    rtTimerRef.current = setInterval(loadRealTime, 5000);
    return () => clearInterval(rtTimerRef.current);
  }, [loadRealTime]);

  // ── Build geo viewer map (numericId → viewers) ────────────────────────────
  const geoMap = React.useMemo(() => {
    const m = {};
    (geo.data || []).forEach(d => { if (d.numericId) m[d.numericId] = d.viewers; });
    return m;
  }, [geo.data]);

  const maxGeoViewers = React.useMemo(
    () => Math.max(1, ...Object.values(geoMap)),
    [geoMap]
  );

  // ── Export CSV ────────────────────────────────────────────────────────────
  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const res = await axios.get(`/api/analytics/export?period=${period}`, {
        ...authHeader(), responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `viewers-${period}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV exportado');
    } catch (e) { toast.error('Error exportando CSV'); }
    finally { setIsExporting(false); }
  };

  // ── Export PDF ────────────────────────────────────────────────────────────
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const res = await axios.get(`/api/analytics/export-pdf-data?period=${period}`, authHeader());
      const d = res.data;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W = doc.internal.pageSize.getWidth();

      // Header
      doc.setFillColor(99, 102, 241);
      doc.rect(0, 0, W, 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20); doc.setFont('helvetica', 'bold');
      doc.text('StreamCP Pro — Reporte de Analíticas', W / 2, 13, { align: 'center' });
      doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      const periodLabel = {
        '24h': 'Últimas 24 horas',
        'day': 'Hoy',
        '7d': 'Últimos 7 días',
        '30d': 'Últimos 30 días',
        'month': 'Este mes',
        'year': 'Este año'
      }[d.period] || d.period;
      doc.text(`Período: ${periodLabel} | Generado: ${new Date(d.generatedAt).toLocaleString('es-ES')}`, W / 2, 22, { align: 'center' });

      let y = 40;

      // Summary
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(14); doc.setFont('helvetica', 'bold');
      doc.text('Resumen General', 14, y); y += 8;

      const avg = Math.round(d.summary?.avg_watch || 0);
      autoTable(doc, {
        startY: y,
        head: [['Métrica', 'Valor']],
        body: [
          ['Canales activos', d.summary?.channels || 0],
          ['Personas únicas reales', d.summary?.unique_viewers || 0],
          ['Total eventos', d.summary?.total_events || 0],
          ['Sesiones de visualización', d.summary?.sessions || 0],
          ['Tiempo promedio de visualización', `${Math.floor(avg / 60)}m ${avg % 60}s`],
        ],
        theme: 'striped',
        headStyles: { fillColor: [99, 102, 241] },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 10;

      // Devices
      if (d.devices?.length > 0) {
        doc.setFontSize(14); doc.setFont('helvetica', 'bold');
        doc.text('Análisis por Dispositivo', 14, y); y += 6;
        autoTable(doc, {
          startY: y,
          head: [['Dispositivo', 'Sesiones']],
          body: d.devices.map(dev => [dev.label || dev.device, dev.count]),
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246] },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      // Geo
      if (d.geo?.length > 0) {
        if (y > 220) { doc.addPage(); y = 20; }
        doc.setFontSize(14); doc.setFont('helvetica', 'bold');
        doc.text('Audiencia por País', 14, y); y += 6;
        autoTable(doc, {
          startY: y,
          head: [['País', 'Viewers']],
          body: d.geo.map(g => [`${g.flag} ${g.name}`, g.viewers]),
          theme: 'striped',
          headStyles: { fillColor: [16, 185, 129] },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      // Peak hours
      if (d.peaks?.length > 0) {
        if (y > 220) { doc.addPage(); y = 20; }
        doc.setFontSize(14); doc.setFont('helvetica', 'bold');
        doc.text('Picos de Tráfico (top horas)', 14, y); y += 6;
        autoTable(doc, {
          startY: y,
          head: [['Hora', 'Eventos']],
          body: d.peaks.map(p => [p.hour, p.events]),
          theme: 'striped',
          headStyles: { fillColor: [245, 158, 11] },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      // Avg watch per channel
      if (d.avgWatch?.length > 0) {
        if (y > 220) { doc.addPage(); y = 20; }
        doc.setFontSize(14); doc.setFont('helvetica', 'bold');
        doc.text('Tiempo Promedio por Canal', 14, y); y += 6;
        autoTable(doc, {
          startY: y,
          head: [['Canal', 'Sesiones', 'Tiempo Promedio']],
          body: d.avgWatch.map(w => [w.name, w.sessions, w.avgFormatted]),
          theme: 'striped',
          headStyles: { fillColor: [236, 72, 153] },
          margin: { left: 14, right: 14 },
        });
      }

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8); doc.setTextColor(150);
        doc.text(`StreamCP Pro — Página ${i} de ${pageCount}`, W / 2, 292, { align: 'center' });
      }

      doc.save(`analytics-${period}-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exportado correctamente');
    } catch (e) {
      console.error('PDF error:', e);
      toast.error('Error exportando PDF');
    } finally {
      setIsExporting(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const StatCard = ({ label, value, sub, icon: Icon, gradient, subColor = 'text-gray-400' }) => (
    <div className="stat-card-premium group">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="text-gray-400 text-sm mb-1 font-medium">{label}</div>
          <div className="text-2xl font-bold text-white mb-2">{value}</div>
          {sub && <div className={`text-xs ${subColor}`}>{sub}</div>}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg flex-shrink-0`}>
          <Icon size={22} className="text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex justify-center items-center h-screen">
          <div className="loading w-10 h-10" />
        </div>
      </Layout>
    );
  }

  // ── TAB: Resumen ──────────────────────────────────────────────────────────
  const TabOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Canales"     value={stats?.channel_stats?.total_channels || 0}
          sub={`${trends.channels >= 0 ? '+' : ''}${trends.channels}% esta semana`}
          icon={BarChart3} gradient="from-accent-purple to-accent-indigo"
          subColor={trends.channels >= 0 ? 'text-green-400' : 'text-red-400'} />
        <StatCard label="Personas Únicas"   value={stats?.viewer_stats?.unique_viewers || 0}
          sub={`${trends.viewers >= 0 ? '+' : ''}${trends.viewers}% esta semana`}
          icon={Users} gradient="from-pink-500 to-red-500"
          subColor={trends.viewers >= 0 ? 'text-green-400' : 'text-red-400'} />
        <StatCard label="Pico Concurrente"  value={stats?.viewer_stats?.peak_concurrent || 0}
          sub={`Promedio: ${stats?.viewer_stats?.avg_concurrent || 0}`}
          icon={Activity} gradient="from-accent-indigo to-blue-500" />
        <StatCard label="Tiempo Promedio"
          value={(() => { const s = Math.round(stats?.session_stats?.avg_watch_seconds || 0); return `${Math.floor(s/60)}m ${s%60}s`; })()}
          sub={`${stats?.session_stats?.total_sessions || 0} sesiones`}
          icon={Clock} gradient="from-amber-500 to-orange-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card">
          <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
            <Activity size={18} className="text-accent-purple" />
            Actividad {period === '24h' ? 'por Hora' : period === '7d' ? 'Semanal' : 'Mensual'}
          </h3>
          {activityData.length === 0 ? NO_DATA_PLACEHOLDER() : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="name" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} />
                <Tooltip {...TOOLTIP_STYLE} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                <Legend />
                <Bar dataKey="viewers" name="Personas únicas"  fill="#6366f1" radius={[6,6,0,0]} />
                <Bar dataKey="peak" name="Pico concurrente"  fill="#10b981" radius={[6,6,0,0]} />
                <Bar dataKey="streams" name="Streams"  fill="#8b5cf6" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="glass-card">
          <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
            <BarChart3 size={18} className="text-accent-indigo" />
            Distribución de Contenido
          </h3>
          {contentDist.length === 0 ? NO_DATA_PLACEHOLDER('No hay contenido') : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={contentDist} cx="50%" cy="50%" outerRadius={90}
                  label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}
                  labelLine={false} dataKey="value">
                  {contentDist.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip {...TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );

  // ── TAB: Tiempo Real ──────────────────────────────────────────────────────
  const TabRealTime = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
        Actualizando cada 5 segundos
      </div>

      {/* Main RT metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Usuarios Conectados" value={realTime?.totalViewers ?? '—'}
          sub="viewers activos" icon={Users} gradient="from-green-500 to-emerald-600" />
        <StatCard label="Canales Live"         value={realTime?.activeChannels ?? '—'}
          sub={`de ${realTime?.channels?.total || 0} canales`} icon={Activity} gradient="from-accent-purple to-accent-indigo" />
        <StatCard label="Ancho de Banda"       value={realTime?.bandwidth || '—'}
          sub="consumo actual" icon={Wifi} gradient="from-blue-500 to-cyan-500" />
        <StatCard label="T. Prom. Visualización" value={realTime?.avgWatchFormatted || '0m 0s'}
          sub="últimas 24h" icon={Clock} gradient="from-amber-500 to-orange-500" />
      </div>

      {/* System stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* CPU */}
        <div className="glass-card">
          <h4 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
            <Cpu size={16} className="text-accent-purple" /> CPU
          </h4>
          <div className="text-3xl font-bold text-white mb-2">{realTime?.system?.cpu?.usage ?? 0}%</div>
          <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-accent-purple to-accent-indigo rounded-full transition-all duration-700"
              style={{ width: `${Math.min(100, realTime?.system?.cpu?.usage || 0)}%` }} />
          </div>
          <div className="text-xs text-gray-400 mt-2">{realTime?.system?.cpu?.cores} núcleos · {realTime?.system?.cpu?.model?.split(' ').slice(0,4).join(' ')}</div>
        </div>

        {/* RAM */}
        <div className="glass-card">
          <h4 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
            <Activity size={16} className="text-pink-400" /> Memoria RAM
          </h4>
          <div className="text-3xl font-bold text-white mb-2">{realTime?.system?.memory?.usagePercent ?? 0}%</div>
          <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-pink-500 to-red-500 rounded-full transition-all duration-700"
              style={{ width: `${Math.min(100, parseFloat(realTime?.system?.memory?.usagePercent || 0))}%` }} />
          </div>
          <div className="text-xs text-gray-400 mt-2">{realTime?.system?.memory?.used} / {realTime?.system?.memory?.total}</div>
        </div>

        {/* Disk */}
        <div className="glass-card">
          <h4 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
            <HardDrive size={16} className="text-amber-400" /> Disco
          </h4>
          <div className="text-3xl font-bold text-white mb-2">{realTime?.system?.disk?.usagePercent ?? 0}%</div>
          <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-700"
              style={{ width: `${Math.min(100, realTime?.system?.disk?.usagePercent || 0)}%` }} />
          </div>
          <div className="text-xs text-gray-400 mt-2">{realTime?.system?.disk?.used} / {realTime?.system?.disk?.total}</div>
        </div>
      </div>

      {/* Server uptime + VODs */}
      <div className="grid grid-cols-2 gap-4">
        <StatCard label="Uptime del Servidor" value={realTime?.uptime || '—'}
          sub="tiempo activo" icon={Clock} gradient="from-teal-500 to-cyan-600" />
        <StatCard label="Total VODs"   value={realTime?.vods?.total ?? '—'}
          sub={`${realTime?.vods?.views || 0} vistas`} icon={FileText} gradient="from-violet-500 to-purple-600" />
      </div>
    </div>
  );

  // ── TAB: Audiencia ────────────────────────────────────────────────────────
  const TabAudience = () => (
    <div className="space-y-6">
      {/* Avg watch time per channel */}
      <div className="glass-card">
        <h3 className="text-lg font-semibold mb-1 text-white flex items-center gap-2">
          <Clock size={18} className="text-amber-400" /> Tiempo Promedio de Visualización
        </h3>
        <p className="text-xs text-gray-400 mb-4">Por canal — basado en sesiones de viewers</p>
        {!avgWatch.hasData ? NO_DATA_PLACEHOLDER() : (
          <div className="space-y-3">
            <div className="text-center mb-4">
              <span className="text-3xl font-bold text-amber-400">{avgWatch.globalAvgFormatted}</span>
              <p className="text-xs text-gray-400 mt-1">Promedio global</p>
            </div>
            {avgWatch.data.map((ch, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="text-sm text-gray-300 w-32 truncate flex-shrink-0">{ch.name}</div>
                <div className="flex-1 h-6 bg-gray-700/50 rounded-lg overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg flex items-center px-2 transition-all duration-700"
                    style={{ width: `${Math.min(100, (ch.avgSeconds / Math.max(...avgWatch.data.map(d => d.avgSeconds), 1)) * 100)}%`, minWidth: '40px' }}>
                    <span className="text-xs text-white font-medium whitespace-nowrap">{ch.avgFormatted}</span>
                  </div>
                </div>
                <div className="text-xs text-gray-400 w-16 text-right flex-shrink-0">{ch.sessions} sesiones</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Retention curve */}
      <div className="glass-card">
        <h3 className="text-lg font-semibold mb-1 text-white flex items-center gap-2">
          <TrendingUp size={18} className="text-accent-purple" /> Retención de Audiencia
        </h3>
        <p className="text-xs text-gray-400 mb-4">% de viewers que siguen viendo en cada punto del stream</p>
        {!retention.hasData ? NO_DATA_PLACEHOLDER() : (
          <>
            <div className="flex gap-4 mb-4 text-sm">
              <span className="text-gray-400">Sesiones totales: <span className="text-white font-semibold">{retention.totalSessions}</span></span>
              <span className="text-gray-400">T. promedio: <span className="text-white font-semibold">{retention.avgWatchFormatted}</span></span>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={retention.data}>
                <defs>
                  <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="percent" stroke="#9ca3af" style={{ fontSize: '11px' }}
                  tickFormatter={v => `${v}%`} label={{ value: '% del video', position: 'insideBottom', offset: -2, fill: '#6b7280', fontSize: 11 }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} domain={[0, 100]}
                  tickFormatter={v => `${v}%`} />
                <Tooltip {...TOOLTIP_STYLE} formatter={(v) => [`${v}%`, 'Retención']}
                  labelFormatter={l => `Punto: ${l}%`} />
                <Area type="monotone" dataKey="retention" stroke="#6366f1" strokeWidth={2}
                  fill="url(#retGrad)" name="Retención" />
              </AreaChart>
            </ResponsiveContainer>
          </>
        )}
      </div>
    </div>
  );

  // ── TAB: Tráfico ──────────────────────────────────────────────────────────
  const TabTraffic = () => (
    <div className="space-y-6">
      <div className="glass-card">
        <h3 className="text-lg font-semibold mb-1 text-white flex items-center gap-2">
          <TrendingUp size={18} className="text-blue-400" /> Picos de Tráfico por Hora
        </h3>
        <p className="text-xs text-gray-400 mb-4">Distribución de actividad durante el día (hora del servidor)</p>
        {!trafficPeaks.hasData ? NO_DATA_PLACEHOLDER() : (
          <>
            {trafficPeaks.peak?.viewers > 0 && (
              <div className="mb-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-sm">
                <span className="text-blue-300">Hora pico: </span>
                <span className="text-white font-semibold">{trafficPeaks.peak.hour}</span>
                <span className="text-gray-400"> — {trafficPeaks.peak.viewers} eventos</span>
              </div>
            )}
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={trafficPeaks.data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="hour" stroke="#9ca3af" style={{ fontSize: '10px' }}
                  interval={2} angle={-30} textAnchor="end" height={40} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} />
                <Tooltip {...TOOLTIP_STYLE}
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                  formatter={(v, n) => [v, n === 'viewers' ? 'Viewers' : 'Eventos']}
                  labelFormatter={l => `Hora: ${l}`} />
                <Bar dataKey="viewers" name="viewers" radius={[4,4,0,0]}
                  fill="#6366f1"
                  label={false}>
                  {trafficPeaks.data.map((entry, i) => (
                    <Cell key={i}
                      fill={entry.viewers === trafficPeaks.peak?.viewers && entry.viewers > 0
                        ? '#f59e0b' : '#6366f1'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Time-of-day breakdown */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {['Madrugada','Mañana','Tarde','Noche'].map(label => {
                const total = trafficPeaks.data.filter(d => d.label === label).reduce((a,d) => a + d.viewers, 0);
                return (
                  <div key={label} className="text-center p-3 rounded-xl bg-white/5">
                    <div className="text-lg font-bold text-white">{total}</div>
                    <div className="text-xs text-gray-400">{label}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // ── TAB: Dispositivos ─────────────────────────────────────────────────────
  const DEVICE_ICONS = { desktop: Monitor, mobile: Smartphone, tablet: Tablet, smarttv: Tv, unknown: Activity, other: Activity };

  const TabDevices = () => (
    <div className="space-y-6">
      <div className="glass-card">
        <h3 className="text-lg font-semibold mb-1 text-white flex items-center gap-2">
          <Monitor size={18} className="text-accent-purple" /> Análisis por Dispositivo
        </h3>
        <p className="text-xs text-gray-400 mb-6">Distribución de viewers según tipo de dispositivo</p>

        {!devices.hasData ? NO_DATA_PLACEHOLDER() : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Pie chart */}
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={devices.data} cx="50%" cy="50%"
                  innerRadius={60} outerRadius={100}
                  paddingAngle={3} dataKey="count"
                  label={({ label, percent }) => `${label} ${(percent*100).toFixed(0)}%`}
                  labelLine={true}>
                  {devices.data.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip {...TOOLTIP_STYLE}
                  formatter={(v, n, p) => [v, p.payload.label]} />
              </PieChart>
            </ResponsiveContainer>

            {/* Device list */}
            <div className="space-y-3 flex flex-col justify-center">
              {devices.data.map((d, i) => {
                const Icon = DEVICE_ICONS[d.device] || Activity;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${d.color}22`, border: `1px solid ${d.color}44` }}>
                      <Icon size={16} style={{ color: d.color }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-200">{d.label}</span>
                        <span className="text-white font-semibold">{d.percent}%</span>
                      </div>
                      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${d.percent}%`, background: d.color }} />
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{d.count} sesiones</div>
                    </div>
                  </div>
                );
              })}
              <div className="pt-2 border-t border-white/5 text-xs text-gray-400">
                Total: {devices.total} sesiones registradas
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── TAB: Geografía ────────────────────────────────────────────────────────
  const TabGeo = () => {
    const [tooltip, setTooltip] = useState(null);

    return (
      <div className="space-y-6">
        <div className="glass-card">
          <h3 className="text-lg font-semibold mb-1 text-white flex items-center gap-2">
            <Globe size={18} className="text-green-400" /> Mapa de Audiencia
          </h3>
          <p className="text-xs text-gray-400 mb-4">
            Distribución geográfica de viewers · Detectado por zona horaria del dispositivo
          </p>

          {/* World map */}
          <div className="relative rounded-xl overflow-hidden bg-gray-900/50 border border-white/5"
            style={{ height: '380px' }}>
            <ComposableMap
              projectionConfig={{ scale: 140, center: [0, 10] }}
              style={{ width: '100%', height: '100%', background: 'transparent' }}>
              <ZoomableGroup zoom={1} minZoom={0.5} maxZoom={4}>
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map(geo => {
                      const id = geo.id;
                      const viewers = geoMap[id] || 0;
                      const intensity = viewers > 0 ? Math.min(0.9, 0.3 + (viewers / maxGeoViewers) * 0.7) : 0;
                      const fill = viewers > 0
                        ? `rgba(99,102,241,${intensity})`
                        : 'rgba(55,65,81,0.6)';
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={fill}
                          stroke="rgba(255,255,255,0.08)"
                          strokeWidth={0.4}
                          onMouseEnter={(evt) => {
                            const countryInfo = geo.data?.length
                              ? null
                              : (geo.data || []);
                            const name = geo.properties?.name || geo.id;
                            const geoEntry = (geo.data || []);
                            setTooltip({ name, viewers, x: evt.clientX, y: evt.clientY });
                          }}
                          onMouseMove={(evt) => {
                            if (tooltip) setTooltip(t => t ? { ...t, x: evt.clientX, y: evt.clientY } : null);
                          }}
                          onMouseLeave={() => setTooltip(null)}
                          style={{
                            default: { outline: 'none', cursor: viewers > 0 ? 'pointer' : 'default' },
                            hover:   { fill: viewers > 0 ? 'rgba(139,92,246,0.9)' : 'rgba(75,85,99,0.8)', outline: 'none' },
                            pressed: { outline: 'none' }
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>

            {/* Tooltip */}
            {tooltip && (
              <div className="fixed pointer-events-none z-50 px-3 py-2 rounded-xl text-xs text-white"
                style={{ left: tooltip.x + 12, top: tooltip.y - 40,
                  background: 'rgba(17,24,39,0.97)',
                  border: '1px solid rgba(129,140,248,0.4)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                <div className="font-semibold">{tooltip.name}</div>
                <div className="text-gray-300">{tooltip.viewers > 0 ? `${tooltip.viewers} viewers` : 'Sin datos'}</div>
              </div>
            )}

            {/* Legend */}
            <div className="absolute bottom-3 left-3 flex items-center gap-2 text-xs text-gray-400 bg-gray-900/80 px-2 py-1 rounded-lg backdrop-blur-sm">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-gray-600/60" /> Sin datos
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-indigo-500/40" /> Pocos
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-indigo-500" /> Muchos
              </div>
            </div>
            <div className="absolute top-3 right-3 text-xs text-gray-500">
              Scroll para zoom · Arrastra para mover
            </div>
          </div>
        </div>

        {/* Country table */}
        <div className="glass-card">
          <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
            <MapPin size={18} className="text-green-400" /> Top Países
          </h3>
          {!geo.hasData ? NO_DATA_PLACEHOLDER() : (
            <div className="space-y-2">
              {geo.data.slice(0, 15).map((c, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-gray-400 text-xs w-5 text-right">{i + 1}</span>
                  <span className="text-xl">{c.flag}</span>
                  <span className="text-gray-200 text-sm flex-1">{c.name}</span>
                  <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full"
                      style={{ width: `${c.percent}%` }} />
                  </div>
                  <span className="text-white text-sm font-semibold w-8 text-right">{c.viewers}</span>
                  <span className="text-gray-500 text-xs w-8">{c.percent}%</span>
                </div>
              ))}
              {geo.data.length === 0 && NO_DATA_PLACEHOLDER()}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── TAB: Exportar ─────────────────────────────────────────────────────────
  const TabExport = () => (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="glass-card">
        <h3 className="text-lg font-semibold mb-2 text-white">Reportes Exportables</h3>
        <p className="text-sm text-gray-400 mb-6">Descarga los datos de analíticas del período seleccionado</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* CSV */}
          <button onClick={handleExportCSV} disabled={isExporting}
            className="group relative overflow-hidden rounded-2xl p-6 text-left border border-green-500/30 bg-green-500/5 hover:bg-green-500/10 transition-all disabled:opacity-50">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-4 shadow-lg shadow-green-500/25">
              <FileText size={24} className="text-white" />
            </div>
            <div className="font-semibold text-white mb-1">Exportar CSV</div>
            <div className="text-xs text-gray-400">
              Todos los eventos de analíticas: timestamps, canales, métricas, países, dispositivos y duración de sesión.
            </div>
            <div className="mt-3 text-xs text-green-400 font-medium flex items-center gap-1">
              <Download size={12} /> Descargar .csv
            </div>
          </button>

          {/* PDF */}
          <button onClick={handleExportPDF} disabled={isExporting}
            className="group relative overflow-hidden rounded-2xl p-6 text-left border border-red-500/30 bg-red-500/5 hover:bg-red-500/10 transition-all disabled:opacity-50">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center mb-4 shadow-lg shadow-red-500/25">
              <FileText size={24} className="text-white" />
            </div>
            <div className="font-semibold text-white mb-1">Exportar PDF</div>
            <div className="text-xs text-gray-400">
              Reporte completo en PDF con: resumen ejecutivo, dispositivos, audiencia por país, picos de tráfico y tiempo promedio por canal.
            </div>
            <div className="mt-3 text-xs text-red-400 font-medium flex items-center gap-1">
              <Download size={12} /> Descargar .pdf
            </div>
          </button>
        </div>

        {isExporting && (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
            <div className="loading w-4 h-4" /> Generando reporte...
          </div>
        )}
      </div>

      {/* Info note */}
      <div className="rounded-xl bg-blue-500/10 border border-blue-500/20 p-4 text-sm text-blue-300">
        <div className="font-semibold mb-1">Cómo se recopilan los datos</div>
        <p className="text-xs text-blue-300/70">
          Los datos de audiencia (retención, dispositivos, geografía) se registran automáticamente cuando los
          viewers usan el <strong>Player Embebido</strong> público. El mapa geográfico se basa en la zona horaria
          del dispositivo del viewer. Los datos de tráfico y actividad se registran en tiempo real.
        </p>
      </div>
    </div>
  );

  const TAB_CONTENT = {
    overview:  <TabOverview />,
    realtime:  <TabRealTime />,
    audience:  <TabAudience />,
    traffic:   <TabTraffic />,
    devices:   <TabDevices />,
    geo:       <TabGeo />,
    export:    <TabExport />,
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-2.5 min-h-screen max-w-[1600px] mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-1">Analíticas</h1>
          <p className="text-gray-400 text-sm flex items-center gap-2">
            <BarChart3 size={16} className="text-accent-purple" />
            Métricas, estadísticas y reportes de rendimiento
          </p>
        </div>

        {/* Controls bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <select className="input-glass text-sm py-2 px-3" value={period}
              onChange={e => setPeriod(e.target.value)}>
              <option value="day">Hoy</option>
              <option value="24h">Últimas 24h</option>
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
              <option value="month">Este mes</option>
              <option value="year">Este año</option>
            </select>
            <button onClick={loadData}
              className="p-2 rounded-xl glass-card border border-white/10 hover:border-accent-purple/40 transition-all text-gray-400 hover:text-white"
              title="Actualizar datos">
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Real-time dot */}
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            Live · {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-1 scrollbar-hide">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  active
                    ? 'bg-gradient-to-r from-accent-purple to-accent-indigo text-white shadow-lg shadow-accent-purple/25'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}>
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {TAB_CONTENT[activeTab]}
      </div>
    </Layout>
  );
}

export default Analytics;
